import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDownRight } from "lucide-react";
import { MaskedLines, SectionHead, Reveal, Eyebrow, ClaimBadge } from "@/components/site/Primitives";
import { AtAGlance, StatusTimeline, AudienceBand, ProofGrid, Frame, NewsList } from "@/components/site/Modules";
import { NEWS, HOME_COPY } from "@/lib/content";
import { Container } from "@/components/site/Layout";
import { FlagRule } from "@/components/site/Logo";
import { useHeroVideo, HeroVideo, VideoToggle } from "@/components/site/HeroVideo";
import { ContactRouting } from "@/components/site/ContactRouting";
import { JobRow, useJobs } from "@/components/site/JobBoard";
import { jobId } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { IntroSplash, useIntro } from "@/components/site/IntroSplash";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const video = useHeroVideo();
  return (
    <section ref={ref} data-testid="home-hero" className="relative mt-[116px] flex min-h-[calc(100svh-116px)] items-center overflow-hidden text-white">
      <motion.div style={{ y }} className="absolute inset-0 scale-110"><HeroVideo video={video} /></motion.div>
      <div className="absolute inset-0 bg-[#001870]/45" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#001870]/85 via-[#001870]/40 to-transparent" />
      <div className="relative mx-auto w-full max-w-[1400px] px-5 py-20 md:px-10">
        <h1 data-testid="home-hero-title" className="font-display text-[4.2rem] font-bold uppercase leading-[0.85] drop-shadow-[0_4px_18px_rgba(0,0,0,0.35)] sm:text-8xl lg:text-[9rem]">
          <MaskedLines lines={["Refining.", <>Redefined.<sup className="-ml-[1.3em] inline-block align-top text-[0.2em] font-bold leading-none [vertical-align:2.25em]">™</sup></>]} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.8 }} className="mt-5 text-lg font-light md:text-2xl lg:whitespace-nowrap">{HOME_COPY.tagline}</motion.p>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="mt-9 flex flex-wrap gap-3">
          <Link to="/brownsville-facility" data-testid="hero-cta-project" className="group inline-flex items-center gap-2 bg-white px-6 py-4 text-sm font-semibold uppercase tracking-wide text-[#001870] transition-colors hover:bg-[#E3BC6E]">The Brownsville facility <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
          <a href="#intro" data-testid="hero-cta-routes" className="inline-flex items-center gap-2 border border-white px-6 py-4 text-sm font-semibold uppercase tracking-wide transition-colors hover:bg-white/10">Learn more <ArrowDownRight className="h-4 w-4" /></a>
        </motion.div>
        <VideoToggle video={video} className="mt-12" />
      </div>
      <FlagRule className="absolute inset-x-0 bottom-0 opacity-90" />
    </section>
  );
};

const Intro = () => (
  <Container id="intro" testId="intro-section">
    <div className="grid gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-5">
        <Eyebrow>About the refinery</Eyebrow>
        <h2 className="font-display mt-4 text-4xl font-bold uppercase leading-[0.95] sm:text-5xl lg:text-6xl">The first new refinery on the Gulf of America in over 47 years</h2>
      </Reveal>
      <Reveal delay={0.1} className="space-y-5 text-base leading-relaxed md:text-lg lg:col-span-7">
        {HOME_COPY.intro.map((p) => <p key={p.slice(0, 30)}>{p}</p>)}
      </Reveal>
    </div>
    <div data-testid="intro-stats" className="mt-14 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
      {HOME_COPY.stats.map((s, i) => (
        <Reveal key={s.stat} delay={i * 0.06} className="bg-white p-7">
          <ClaimBadge type={s.projected ? "projected" : "fact"} testId={`intro-stat-badge-${i}`} />
          <p className="font-display mt-4 text-6xl font-bold text-[#001870]">{s.stat}</p>
          <p className="mt-2 text-sm">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </Container>
);

const Tech = () => (
  <Container testId="tech-section" tone="soft">
    <Reveal className="mx-auto max-w-4xl text-center">
      <h2 className="font-display text-4xl font-bold uppercase leading-[0.95] sm:text-5xl lg:text-6xl">Advancing American fuel production through modern technology</h2>
    </Reveal>
    <div className="mt-14 grid gap-6 md:grid-cols-3">
      {HOME_COPY.tech.map((t, i) => (
        <Reveal key={t.title} delay={i * 0.08} className="group border-t-4 border-[#AD7F27] bg-white p-8 text-center shadow-[0_10px_30px_rgba(0,24,112,0.06)] transition-transform duration-500 hover:-translate-y-1">
          <img src={t.icon} alt="" aria-hidden="true" className="mx-auto h-16 w-16" />
          <h3 className="font-display mt-5 text-2xl font-bold uppercase">{t.title}</h3>
          <p className="mt-2 text-sm leading-relaxed">{t.body}</p>
        </Reveal>
      ))}
    </div>
  </Container>
);

const Pillars = () => (
  <Container testId="pillars-section" tone="dark">
    <div className="grid gap-px border border-white/15 bg-white/15 sm:grid-cols-2 lg:grid-cols-4">
      {HOME_COPY.pillars.map((p, i) => (
        <Reveal key={p.title} delay={i * 0.06} className="bg-[#001870] p-8">
          <p className="font-display text-5xl font-bold text-[#E3BC6E]">0{i + 1}</p>
          <h3 className="font-display mt-4 text-3xl font-bold uppercase leading-none">{p.title}</h3>
          <p className="mt-3 text-sm text-white/85">{p.body}</p>
        </Reveal>
      ))}
    </div>
  </Container>
);

const Hydrogen = () => (
  <Container testId="hydrogen-section">
    <div className="grid items-center gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-6">
        <h2 className="font-display text-4xl font-bold uppercase leading-[0.95] sm:text-5xl lg:text-6xl">Clean fuels from American shale, powered by hydrogen</h2>
        <div className="mt-6 space-y-4 leading-relaxed">{HOME_COPY.hydrogen.map((p) => <p key={p.slice(0, 30)}>{p}</p>)}</div>
        <ClaimBadge type="projected" testId="hydrogen-badge" />
      </Reveal>
      <Frame src="/img/afr-tanks-trees.jpg" alt="Rendering of AFR storage tanks among trees at the Brownsville site" caption="Rendering · AFR" className="h-[300px] md:h-[460px] lg:col-span-6" />
    </div>
  </Container>
);

const Leadership = () => (
  <Container testId="leadership-band" tone="dark">
    <div className="grid items-center gap-12 lg:grid-cols-12">
      <Frame src="/img/afr-team.jpg" alt="Engineers in hard hats reviewing work on site" className="h-[320px] md:h-[480px] lg:col-span-6" />
      <Reveal className="lg:col-span-6">
        <h2 className="font-display text-4xl font-bold uppercase leading-[0.95] sm:text-5xl lg:text-6xl">World-class leadership driving industry innovation</h2>
        <p className="mt-6 leading-relaxed text-white/90">{HOME_COPY.leadership}</p>
        <Link to="/leadership" data-testid="leadership-team-cta" className="group mt-8 inline-flex items-center gap-2 border border-white px-6 py-4 text-sm font-semibold uppercase tracking-wide transition-colors hover:bg-white hover:text-[#001870]">Leadership team <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
      </Reveal>
    </div>
  </Container>
);

const CareersPreview = () => {
  const jobs = useJobs();
  return (
    <Container testId="careers-preview" tone="soft">
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <SectionHead eyebrow="Careers" title="Hiring in Brownsville" body="Roles are posted individually, with an owner and a closing date. Closed roles come down the same day." />
          <Link to="/careers" data-testid="careers-preview-cta" className="group inline-flex items-center gap-2 border-b-2 border-[#AD7F27] pb-1 font-semibold text-[#001870]">All open roles <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
        </div>
        <div className="border-t border-white/10 lg:col-span-8">
          {(jobs ?? []).slice(0, 3).map((j, i) => <JobRow key={jobId(j)} job={j} i={i} />)}
          {jobs !== null && jobs.length === 0 && <p className="py-8 text-slate-300">No roles open right now.</p>}
        </div>
      </div>
    </Container>
  );
};

export default function Landing() {
  useSeo({
    title: "America First Refining | Brownsville, Texas Refinery Project",
    description: "America First Refining is building a refinery at the Port of Brownsville, Texas, designed to process 100% American shale oil into cleaner fuels.",
    path: "/",
    jsonLd: { "@context": "https://schema.org", "@type": "Organization", name: "America First Refining", url: "https://americafirstrefining.com", logo: "https://americafirstrefining.com/wp-content/uploads/2024/05/AFR_logo1.svg", sameAs: ["https://x.com/AFRefining", "https://www.instagram.com/americafirstrefining/", "https://truthsocial.com/@AmericaFirstRefining"], address: { "@type": "PostalAddress", streetAddress: "24 Greenway Plaza, Suite 405", addressLocality: "Houston", addressRegion: "TX", postalCode: "77046", addressCountry: "US" } },
  });
  const intro = useIntro();
  return (
    <div data-testid="home-page">
      {intro.show && <IntroSplash intro={intro} />}
      <Hero key={intro.done ? "hero" : "hero-pending"} />
      <Intro />
      <Tech />
      <Pillars />
      <Hydrogen />
      <Container testId="glance-section" id="glance" tone="soft">
        <SectionHead eyebrow="Project at a glance" title="What, where, and how far along" body="Six facts a resident, partner or investor should be able to read in thirty seconds. Each one carries its type." />
        <AtAGlance />
      </Container>
      <Container id="status" testId="status-section">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead eyebrow="Milestones" title="Dated, not decorated" body="A running record. Past milestones are dated; future ones are labeled as targets." />
            <Frame src="/img/afr-site-2026-03.jpg" alt="Artist rendering of the America First Refining facility with storage tanks beside the ship channel" caption="Artist rendering · AFR, March 2026. Not a photograph." className="h-[320px] md:h-[420px]" />
          </div>
          <div className="lg:col-span-7 lg:pt-24"><StatusTimeline /></div>
        </div>
      </Container>
      <Leadership />
      <Container id="routes" testId="routes-section">
        <SectionHead eyebrow="Choose your route" title="What brings you here?" />
        <AudienceBand />
      </Container>
      <Container id="impact" testId="impact-section" tone="soft">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead eyebrow="Proof, not promises" title="Impact you can check" body="Port facts are current. Job figures are projections from AFR's own materials and are shown that way until approved methodology is published." />
          </div>
          <div className="lg:col-span-8"><ProofGrid /></div>
        </div>
      </Container>
      <Container id="news" testId="news-section">
        <SectionHead eyebrow="In the news" title="Independent coverage" body="Reporting from outside outlets, linked at the source. Headlines are theirs, not AFR claims." />
        <NewsList items={NEWS.slice(0, 6)} />
      </Container>
      <CareersPreview />
      <Container testId="contact-section" tone="dark">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <Eyebrow>Learn more</Eyebrow>
            <h2 className="font-display mt-4 text-5xl font-bold uppercase leading-[0.95] lg:text-6xl">Reach out for more info</h2>
            <p className="mt-5 text-white/85">Pick who you are and your message goes to the team for that audience, with a reference number back to you.</p>
          </Reveal>
          <div className="lg:col-span-8"><ContactRouting /></div>
        </div>
      </Container>
    </div>
  );
}
